import type { MulticallRetryFunction } from '../types'

/**
 * Vault interface family, detected via ERC-165 `supportsInterface`.
 *
 *  - `erc4626`  — plain synchronous tokenized vault (the default; most
 *     vaults don't implement ERC-165 at all, which we treat as 4626).
 *  - `erc7540`  — asynchronous deposit and/or redeem (request → claim).
 *  - `erc7575`  — multi-asset vault (share token split from entrypoints)
 *     that is NOT also 7540.
 */
export type InterfaceKind = 'erc4626' | 'erc7540' | 'erc7575'

/**
 * ERC-165 interface IDs, verified against EIP-7540 / EIP-7575:
 *  - `0xe3bc4e65` — ERC-7540 operator methods (implemented by ALL 7540
 *    vaults, async-deposit and async-redeem alike). Best single probe
 *    for "is this a 7540 vault".
 *  - `0x2f0a18c5` — ERC-7575 interface. All 7540 vaults also return true
 *    here, so 7575-but-not-7540 is the pure multi-asset (sync) case.
 */
export const INTERFACE_IDS = {
  erc7540Operator: '0xe3bc4e65',
  erc7575: '0x2f0a18c5',
} as const

const SupportsInterfaceAbi = [
  {
    name: 'supportsInterface',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'bytes4', name: 'interfaceId' }],
    outputs: [{ type: 'bool' }],
  },
] as const

/**
 * Classify each vault address by interface family using two ERC-165
 * probes per vault (`supportsInterface(0xe3bc4e65)` for 7540,
 * `supportsInterface(0x2f0a18c5)` for 7575). Failures / reverts (a vault
 * that doesn't implement ERC-165) fall back to `erc4626`.
 *
 * Returns a map keyed by lowercased address. Pure read — one multicall.
 */
export const detectInterfaceKinds = async (
  addresses: string[],
  chainId: string,
  multicallRetry: MulticallRetryFunction,
): Promise<Record<string, InterfaceKind>> => {
  const out: Record<string, InterfaceKind> = {}
  if (addresses.length === 0) return out

  const lc = addresses.map((a) => a.toLowerCase())
  // Two calls per vault, interleaved: [7540, 7575, 7540, 7575, …].
  const calls = lc.flatMap((address) => [
    {
      address,
      name: 'supportsInterface',
      params: [INTERFACE_IDS.erc7540Operator],
    },
    { address, name: 'supportsInterface', params: [INTERFACE_IDS.erc7575] },
  ])

  let results: unknown[]
  try {
    results = (await multicallRetry({
      chain: chainId,
      calls: calls as never[],
      abi: SupportsInterfaceAbi as never,
      maxRetries: 3,
      allowFailure: true,
    })) as unknown[]
  } catch {
    // Whole-batch failure → everything defaults to 4626.
    for (const a of lc) out[a] = 'erc4626'
    return out
  }

  for (let i = 0; i < lc.length; i++) {
    const is7540 = results[i * 2] === true
    const is7575 = results[i * 2 + 1] === true
    out[lc[i]] = is7540 ? 'erc7540' : is7575 ? 'erc7575' : 'erc4626'
  }
  return out
}
